/*
  Early access gate — progressive enhancement for the signup panel.

  The lock/unlock decision itself runs inline in sections/early-access-gate.liquid
  so it happens before the product grid is parsed. This file only handles the
  signup form: it posts Shopify's native customer form in the background so the
  grid can be revealed without a page reload.

  If this file fails to load or the submit handler throws, the form still posts
  normally and the server-rendered success state grants access on the next load.
*/
if (!customElements.get('early-access-gate')) {
  customElements.define(
    'early-access-gate',
    class EarlyAccessGate extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.successMessage = this.querySelector('[data-early-access-message="success"]');
        this.errorMessage = this.querySelector('[data-early-access-message="error"]');
      }

      connectedCallback() {
        if (!this.form) return;

        this.form.addEventListener('submit', this.onSubmit.bind(this));

        // The form was posted the old way (no JS at submit time, or a background
        // post that fell back to a normal one) and Shopify rendered the success
        // state on this load — honour it.
        if (this.successMessage && !this.successMessage.hidden) {
          this.classList.add('early-access-gate--success');
          this.grantAccess();
        }
      }

      onSubmit(event) {
        event.preventDefault();
        this.setBusy(true);
        this.toggleMessage(this.errorMessage, false);

        fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'text/html' },
        })
          .then((response) => {
            // Shopify redirects a successful customer signup back with
            // ?customer_posted=true. Anything else is treated as a failure so
            // the shopper can correct their address and try again.
            if (!response.ok || response.url.indexOf('customer_posted=true') === -1) {
              throw new Error('Early access signup did not complete');
            }

            this.onSuccess();
          })
          .catch(() => {
            this.setBusy(false);
            this.toggleMessage(this.errorMessage, true);
            if (this.errorMessage) this.errorMessage.focus();
          });
      }

      onSuccess() {
        this.setBusy(false);
        this.grantAccess();
        this.classList.add('early-access-gate--success');
        this.toggleMessage(this.successMessage, true);
        if (this.successMessage) this.successMessage.focus();
      }

      grantAccess() {
        try {
          window.localStorage.setItem(this.dataset.storageKey, 'true');
        } catch (error) {
          // Storage can be unavailable (private browsing). Access still applies
          // to this page view; the shopper just gets asked again next visit.
        }

        document.documentElement.classList.remove(this.dataset.lockedClass);
      }

      setBusy(isBusy) {
        if (!this.submitButton) return;

        this.submitButton.disabled = isBusy;
        this.submitButton.setAttribute('aria-busy', isBusy);
      }

      toggleMessage(element, isVisible) {
        if (!element) return;

        element.hidden = !isVisible;
      }
    }
  );
}
